import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { DollarSign, CheckCircle, Clock, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import LogoLoader from '@/components/LogoLoader';
import { formatDisplayDate } from '@/utils/date';

const ConsultantPayments = () => {
  const { token, user } = useAuth();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [filterConsultant, setFilterConsultant] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [historyData, consultantsData] = await Promise.all([
        api.get('/time-entries/payment-history?months=3', token),
        api.get('/consultants', token)
      ]);
      setPaymentHistory(historyData);
      setConsultants(consultantsData);
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (selectedEntries.length === 0) {
      toast.error('Please select time entries to mark as paid');
      return;
    }

    try {
      const result = await api.post('/time-entries/mark-paid', selectedEntries, token);
      toast.success(`Marked ${result.entries_marked} entries as paid. Total: $${result.total_amount.toLocaleString()}`);
      setSelectedEntries([]);
      loadData();
    } catch (error) {
      toast.error('Failed to mark payments');
    }
  };

  const handleSelectEntry = (entryId) => {
    setSelectedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const unpaidEntries = filteredData
        .filter(entry => entry.payment_status === 'Unpaid')
        .map(entry => entry.id);
      setSelectedEntries(unpaidEntries);
    } else {
      setSelectedEntries([]);
    }
  };

  // Filter data
  const filteredData = paymentHistory.filter(entry => {
    if (filterConsultant !== 'all' && entry.consultant_id !== filterConsultant) return false;
    if (filterStatus !== 'all' && entry.payment_status !== filterStatus) return false;
    return true;
  });

  // Calculate summary
  const totalUnpaid = filteredData
    .filter(e => e.payment_status === 'Unpaid')
    .reduce((sum, e) => sum + e.payment_amount, 0);
  
  const totalPaid = filteredData
    .filter(e => e.payment_status === 'Paid')
    .reduce((sum, e) => sum + e.payment_amount, 0);
  
  const selectedAmount = paymentHistory
    .filter(e => selectedEntries.includes(e.id))
    .reduce((sum, e) => sum + e.payment_amount, 0);

  if (loading) {
    return <LogoLoader message="Loading consultant payments..." />;
  }

  return (
    <div className="space-y-6" data-testid="consultant-payments-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Consultant Payments</h1>
        <p className="text-gray-600 mt-1">Last 3 months time entry payment tracking</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-900">Total Unpaid</p>
                <p className="text-2xl font-bold text-red-900 mt-2">
                  ${totalUnpaid.toLocaleString()}
                </p>
              </div>
              <Clock className="w-8 h-8 text-red-700" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900">Total Paid</p>
                <p className="text-2xl font-bold text-green-900 mt-2">
                  ${totalPaid.toLocaleString()}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-700" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-utis-sage bg-utis-sage/10">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-utis-forest">Selected Amount</p>
                <p className="text-2xl font-bold text-utis-forest mt-2">
                  ${selectedAmount.toLocaleString()}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {selectedEntries.length} entries selected
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-utis-forest" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Filter className="w-5 h-5 mr-2 text-utis-forest" />
              Payment History
            </CardTitle>
            {isAdmin && selectedEntries.length > 0 && (
              <Button
                onClick={handleMarkPaid}
                className="bg-utis-forest hover:bg-utis-forest/90 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark {selectedEntries.length} as Paid
              </Button>
            )}
          </div>
          <CardDescription>Time entries from the last 3 months with payment status</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label>Filter by Consultant</Label>
              <Select value={filterConsultant} onValueChange={setFilterConsultant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Consultants</SelectItem>
                  {consultants.filter(c => c.is_active).map(consultant => (
                    <SelectItem key={consultant.id} value={consultant.id}>
                      {consultant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filter by Payment Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Unpaid">Unpaid Only</SelectItem>
                  <SelectItem value="Paid">Paid Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading payment history...</div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No payment records found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && (
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedEntries.length > 0 && selectedEntries.length === filteredData.filter(e => e.payment_status === 'Unpaid').length}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                  )}
                  <TableHead>Consultant</TableHead>
                  <TableHead>Week Start</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Pay Rate</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Project/PO</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((entry) => (
                  <TableRow key={entry.id} className={entry.payment_status === 'Paid' ? 'bg-green-50/30' : ''}>
                    {isAdmin && (
                      <TableCell>
                        {entry.payment_status === 'Unpaid' && (
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onCheckedChange={() => handleSelectEntry(entry.id)}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{entry.consultant_name}</TableCell>
                    <TableCell>{formatDisplayDate(entry.week_start, '-')}</TableCell>
                    <TableCell>{entry.hours.toFixed(1)} hrs</TableCell>
                    <TableCell>${entry.pay_rate}/hr</TableCell>
                    <TableCell className="font-bold text-utis-forest">
                      ${entry.payment_amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm">
                      {entry.project_name || entry.po_number || '-'}
                    </TableCell>
                    <TableCell>
                      {entry.payment_status === 'Paid' ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Paid
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-red-100 text-red-800">
                          <Clock className="w-3 h-3 mr-1" />
                          Unpaid
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {entry.payment_date ? formatDisplayDate(entry.payment_date, '-') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsultantPayments;
